<template>
  <div class="comment-item">
    <div class="comment-main">
      <div class="comment-avatar">
        <img :src="vLogo" width="30" alt="唯品会V学堂" v-if="comment.commentType === 1" style="width:38px; height:26px;"/>
        <!-- <img :src="comment.user.avatar || defaultAvatar" :alt="comment.user.nickname" v-else/> -->
        <imgWrap :src="comment.user.avatar || defaultAvatar" :alt="comment.user.nickname" :pic="'avatar'" class="img-wrap" v-else/>
      </div>
      <div class="comment-content">
        <div class="comment-meta">
          <span class="comment-author" v-if="comment.commentType === 0">{{comment.user.nickname || '唯品会会员'}} {{comment.user.userId}}
            <span class="user-score" v-if="comment.user.score">评价该课程:{{comment.user.scoreLabel}}</span>
            <div class="comment-time">{{formatDateTime(comment.createTime)}}</div>
          </span>
          <span class="official-tag" v-if="comment.commentType === 1">
            {{ comment.parentId ? '官方' : '唯品会V学堂' }}
            <div class="comment-time">{{formatDateTime(comment.createTime)}}</div>
          </span>
          <p class="action-wrap">
            <!-- 回复按钮（10级以内显示） -->
            <span class="action-btn" v-if="level < 10" @click="toggleReplyInput">回复</span>
            <!-- 删除按钮（仅自己的评论显示） -->
            <span class="action-btn delete-btn" v-if="isOwnComment" @click="handleDelete">删除</span>
          </p>
        </div>
        <div class="comment-text">{{comment.content}}</div>
      </div>
    </div>

    <!-- 回复输入框 -->
    <div class="reply-input-wrap" v-if="isReplying">
      <textarea
        v-model="replyContent"
        :maxlength="maxLength"
        :placeholder="'回复 @' + (comment.commentType === 1 ? '唯品会V学堂' : (comment.user.nickname || comment.user.userId)) + '：'"
        class="comment-textarea small"
      ></textarea>
      <div class="input-footer">
        <span class="char-count">{{replyRemainingChars}} / {{maxLength}}</span>
        <t-button theme="primary" size="small" :loading="submitting" @click="handleReply">提交</t-button>
        <t-button theme="default" size="small" @click="cancelReply">取消</t-button>
      </div>
    </div>

    <!-- 子评论列表（递归） -->
    <div class="comment-replies" v-if="comment.replies && comment.replies.length > 0">
      <div
        v-for="reply in comment.replies"
        :key="reply.commentId"
        class="reply-item"
      >
        <CommentItem
          :comment="reply"
          :course-id="courseId"
          :is-logged-in="isLoggedIn"
          :is-blacklisted="isBlacklisted"
          :level="level + 1"
          @reply-submitted="$emit('reply-submitted', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { MessagePlugin } from 'tdesign-vue-next'
import picEmptyBig from '../assets/img/pic_empty_big.png'
import vLogo from '../assets/img/v-logo.png'
import imgWrap from '@/components/imgWrap'

export default {
  name: 'CommentItem',
  components: { imgWrap },
  props: {
    comment: {
      type: Object,
      required: true
    },
    courseId: {
      type: [Number, String],
      required: true
    },
    isLoggedIn: {
      type: Boolean,
      default: false
    },
    isBlacklisted: {
      type: Boolean,
      default: false
    },
    level: {
      type: Number,
      default: 1
    }
  },
  emits: ['reply-submitted', 'replies-updated'],
  setup(props, { emit }) {
    const store = useStore()
    const submitting = ref(false)
    const replyContent = ref('')
    const isReplying = ref(false)
    const maxLength = 500
    const defaultAvatar = picEmptyBig
    const replyRemainingChars = computed(() => maxLength - replyContent.value.length)

    // 判断是否是自己的评论
    const isOwnComment = computed(() => {
      const currentUserId = store.state.userInfo.uid
      return props.comment.user && props.comment.user.userId === currentUserId
    })

    // 校验评论内容：禁止表情符号和尖括号
    const validateContent = (content) => {
      if (!content) return { valid: true }

      // 检测 emoji 表情符号
      const emojiRegex = /[\u{1F000}-\u{1FFFF}]/u
      if (emojiRegex.test(content)) {
        return { valid: false, message: '评论内容不能包含表情符号' }
      }

      // 检测尖括号（防止XSS和数据库报错）
      if (/[<>]/.test(content)) {
        return { valid: false, message: '评论内容不能包含尖括号 < >' }
      }

      return { valid: true }
    }

    const formatTime = (timeStr) => {
      if (!timeStr) return ''
      const date = new Date(timeStr)
      const now = new Date()
      const diff = now - date
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))

      if (days === 0) {
        const hours = Math.floor(diff / (1000 * 60 * 60))
        if (hours === 0) {
          const minutes = Math.floor(diff / (1000 * 60))
          return minutes > 0 ? `${minutes}分钟前` : '刚刚'
        }
        return `${hours}小时前`
      } else if (days === 1) {
        return '昨天'
      } else if (days === 2) {
        return '前天'
      } else if (days < 7) {
        return `${days}天前`
      } else {
        return timeStr.substring(0, 10)
      }
    }

    // 格式化时间为 年月日时分秒：2026-01-01 00:00:00
    const formatDateTime = (timeStr) => {
      if (!timeStr) return ''
      const date = new Date(timeStr)
      const pad = (n) => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    }

    const toggleReplyInput = () => {
      if (!props.isLoggedIn || props.isBlacklisted) return
      isReplying.value = !isReplying.value
      replyContent.value = ''
    }

    const cancelReply = () => {
      isReplying.value = false
      replyContent.value = ''
    }

    const handleReply = async () => {
      const content = replyContent.value.trim()
      if (!content) return

      // 前端校验：禁止表情和尖括号
      const validation = validateContent(content)
      if (!validation.valid) {
        MessagePlugin.warning(validation.message)
        return
      }

      submitting.value = true
      try {
        await store.dispatch('comment/submitComment', {
          courseId: props.courseId,
          content,
          parentId: props.comment.commentId
        })
        emit('reply-submitted', props.comment.commentId)
        cancelReply()
      } catch (error) {
        console.error('回复提交失败', error)
      } finally {
        submitting.value = false
      }
    }

    const handleDelete = async () => {
      if (!confirm('确定要删除这条评论吗？')) return
      submitting.value = true
      try {
        await store.dispatch('comment/deleteComment', {
          commentId: props.comment.commentId
        })
        emit('reply-submitted')
      } catch (error) {
        console.error('删除评论失败', error)
      } finally {
        submitting.value = false
      }
    }

    return {
      submitting,
      replyContent,
      isReplying,
      isOwnComment,
      maxLength,
      defaultAvatar,
      replyRemainingChars,
      formatTime,
      formatDateTime,
      toggleReplyInput,
      cancelReply,
      handleReply,
      handleDelete,
      vLogo
    }
  }
}
</script>

<style lang="scss" scoped>
.comment-item {
  padding: 8px 0 0;
  
  .comment-main {
    display: flex;
    gap: 8px;
  }

  .comment-avatar {
    width: 40px;
    border-radius: 50%;
    overflow: visible;
    flex-shrink: 0;
    text-align: center;
    
    img {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
    }

    .comment-time {
      font-size: 10px;
      color: #999;
      margin-top: 4px;
      white-space: nowrap;
    }
  }

  .comment-content {
    flex: 1;
    min-width: 0;
  }

  .comment-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;

    .comment-author {
      font-weight: 500;
      color: #333;
      font-size: 14px;

      .user-score {
        color: #FD3374;
        font-size: 12px;
        margin-left: 4px;
      }
    }

    .official-tag {
      background: #FFE6EB;
      color: #FD3374;
      font-size: 12px;
      padding: 2px 6px;
      border-radius: 2px;
    }

    .comment-time {
      color: #999;
      font-size: 12px;
    }
    .action-wrap {
      margin-left: auto;
    }
    .action-btn {
      color: #999;
      font-size: 12px;
      cursor: pointer;
      margin-right: 5px;

      &:hover {
        color: #FD3374;
      }

      &.delete-btn {

      }
    }
  }

  .comment-text {
    color: #333;
    font-size: 14px;
    line-height: 1.6;
    word-break: break-all;
  }

  .reply-input-wrap {
    margin-top: 12px;
    margin-left: 52px;
    padding: 12px;
    background: #F7F8FA;
    border-radius: 8px;

    .comment-textarea {
      width: 100%;
      min-height: 80px;
      border: 1px solid #E7E7E7;
      border-radius: 6px;
      padding: 10px;
      font-size: 14px;
      resize: vertical;
      outline: none;
      transition: border-color 0.2s;

      &:focus {
        border-color: #FD3374;
      }

      &.small {
        min-height: 60px;
      }
    }

    .input-footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;

      .char-count {
        color: #999;
        font-size: 12px;
        margin-right: auto;
      }
    }
  }

  .comment-replies {
    margin-left: 52px;
    padding-left: 8px;
    border-left: 1px dashed #F0F0F0;
    margin-top: 0;

    .reply-item {
      padding: 0 0;
      border-bottom: 1px dashed #F0F0F0;

      &:last-child {
        border-bottom: none;
      }
    }

    .load-more-replies {
      padding: 8px 0;
      color: #FD3374;
      font-size: 12px;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}
.img-wrap {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-block;
}
</style>
